import React, { useState, useEffect } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Message } from 'primereact/message';
import { Divider } from 'primereact/divider';
import { RadioButton } from 'primereact/radiobutton';
import { ProgressSpinner } from 'primereact/progressspinner';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { getGiftProducts, createGiftPurchase, formatPrice, getAgentGiftPurchases } from '../../services/giftService';
import { CustomerService } from '../../services/customerService';
import { useAuth } from '../../contexts/AuthContext';
import type { StripeProduct, AgentGiftPurchase } from '../../types/subscription';
import type { Customer } from '../../types/agent';
import '../../css/gift-purchase.css';

const GiftPurchasePage: React.FC = () => {
  const { currentAgent } = useAuth();
  const [products, setProducts] = useState<StripeProduct[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [purchases, setPurchases] = useState<AgentGiftPurchase[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingPurchases, setLoadingPurchases] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Manual customer entry mode
  const [customerInputMode, setCustomerInputMode] = useState<'select' | 'manual'>('select');
  const [manualEmail, setManualEmail] = useState('');
  const [manualFirstName, setManualFirstName] = useState('');
  const [manualLastName, setManualLastName] = useState('');

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load products and customers in parallel
      const [productsResult, customersResult] = await Promise.all([
        getGiftProducts(),
        CustomerService.getCustomers({
          assignedAgentId: currentAgent?.id,
          limit: 500
        })
      ]);

      setProducts(productsResult.products);
      if (productsResult.products.length > 0 && productsResult.products[0]) {
        setSelectedProductId(productsResult.products[0].productId);
      }

      setCustomers(customersResult.customers);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadPurchases = async () => {
    if (!currentAgent?.id) return;

    try {
      setLoadingPurchases(true);
      const purchaseList = await getAgentGiftPurchases(currentAgent.id);
      setPurchases(purchaseList);
    } catch (err) {
      console.error('Error loading purchase history:', err);
      // Don't set error - it's not critical if purchases fail to load
    } finally {
      setLoadingPurchases(false);
    }
  };

  // Load products and customers on mount
  useEffect(() => {
    loadData();
    loadPurchases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAgent]);

  // Check URL for cancel or success parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('canceled') === 'true') {
      setError('Gift purchase was canceled');
      window.history.replaceState({}, '', '/agent/gifts');
    }
    if (urlParams.get('success') === 'true') {
      // Refresh purchases after successful payment
      loadPurchases();
      window.history.replaceState({}, '', '/agent/gifts');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedProduct = products.find(p => p.productId === selectedProductId);
  const totalAmount = selectedProduct ? selectedProduct.amount : 0;

  // Validate customer selection
  const hasValidCustomer = () => {
    if (customerInputMode === 'select') {
      return !!selectedCustomer;
    } else {
      return !!manualEmail && manualEmail.includes('@');
    }
  };

  const getCustomerInfo = () => {
    if (customerInputMode === 'select' && selectedCustomer) {
      return {
        customerId: selectedCustomer.userId || selectedCustomer.id,
        customerEmail: selectedCustomer.email,
        customerFirstName: selectedCustomer.firstName,
        customerLastName: selectedCustomer.lastName,
      };
    } else if (customerInputMode === 'manual' && manualEmail) {
      return {
        customerEmail: manualEmail,
        customerFirstName: manualFirstName || undefined,
        customerLastName: manualLastName || undefined,
      };
    }
    return {};
  };

  const handlePurchase = async () => {
    if (!selectedProduct) {
      setError('Please select a gift type');
      return;
    }

    if (!hasValidCustomer()) {
      setError('Please select a customer or enter a valid email address');
      return;
    }

    try {
      setProcessing(true);
      setError(null);

      const customerInfo = getCustomerInfo();

      const result = await createGiftPurchase(
        selectedProduct,
        1,
        customerInfo
      );

      if (result.checkoutUrl) {
        // Redirect to Stripe Checkout
        window.location.href = result.checkoutUrl;
      } else {
        setError('Failed to create checkout session');
        setProcessing(false);
      }
    } catch (err) {
      console.error('Error creating gift purchase:', err);
      setError('Failed to start gift purchase checkout');
      setProcessing(false);
    }
  };

  // Template for status column in purchase history
  const statusTemplate = (purchase: AgentGiftPurchase) => {
    if (purchase.status === 'applied') {
      return <Tag severity="success" value="Applied" />;
    }
    return <Tag severity="warning" value="Pending" />;
  };

  // Template for gift type column
  const giftTypeTemplate = (purchase: AgentGiftPurchase) => {
    const typeLabels: Record<string, string> = {
      'subscription_monthly': '1 Month Subscription',
      'subscription_annual': '1 Year Subscription',
      'customer_subscription_annual': '1 Year Customer Sub',
      'assessment_single': 'Single Assessment',
    };
    return typeLabels[purchase.giftType] || purchase.giftType;
  };

  // Template for recipient column
  const recipientTemplate = (purchase: AgentGiftPurchase) => {
    const name = purchase.recipientName || '';
    const email = purchase.recipientEmail || '';
    if (name && email) {
      return (
        <div>
          <div>{name}</div>
          <small className="text-muted">{email}</small>
        </div>
      );
    }
    return email || name || '-';
  };

  // Template for amount column
  const amountTemplate = (purchase: AgentGiftPurchase) => {
    return formatPrice(purchase.totalAmount);
  };

  // Template for date column
  const dateTemplate = (purchase: AgentGiftPurchase) => {
    return new Date(purchase.createdAt).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="gift-purchase-page">
        <div className="loading-container">
          <ProgressSpinner />
          <p>Loading gift products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="gift-purchase-page">
      <div className="page-header">
        <h1>
          <i className="pi pi-gift" style={{ marginRight: '0.5rem' }} />
          Gift Subscription to Customer
        </h1>
        <p>Purchase a subscription or assessment for your customer - applied instantly</p>
      </div>

      {error && (
        <Message severity="error" text={error} style={{ marginBottom: '1rem' }} />
      )}

      {products.length === 0 ? (
        <Message
          severity="info"
          text="No gift products are currently available. Please contact support."
        />
      ) : (
        <>
          <div className="gift-purchase-container">
            <Card className="gift-selection-card">
              <h2>Select Gift Type</h2>
              <Divider />

              <div className="gift-types">
                {products.map((product) => (
                  <div
                    key={product.productId}
                    className={`gift-type-option ${selectedProductId === product.productId ? 'selected' : ''}`}
                    onClick={() => setSelectedProductId(product.productId)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        if (e.key === ' ') e.preventDefault();
                        setSelectedProductId(product.productId);
                      }
                    }}
                  >
                    <RadioButton
                      inputId={product.productId}
                      value={product.productId}
                      checked={selectedProductId === product.productId}
                      onChange={(e) => setSelectedProductId(e.value)}
                    />
                    <div className="gift-type-content">
                      <div className="gift-type-header">
                        <i className={`pi ${product.icon} gift-icon`} />
                        <div>
                          <h3>{product.name}</h3>
                          <p className="gift-price">{formatPrice(product.amount)}</p>
                        </div>
                      </div>
                      <p className="gift-description">{product.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <Divider />

              <div className="gift-options">
                <div className="form-field">
                  <label>
                    Select Customer <span className="required">*</span>
                  </label>

                  <div className="customer-input-toggle">
                    <Button
                      label="Existing Customer"
                      className={customerInputMode === 'select' ? 'p-button-primary' : 'p-button-outlined'}
                      onClick={() => setCustomerInputMode('select')}
                      size="small"
                    />
                    <Button
                      label="Enter Email"
                      className={customerInputMode === 'manual' ? 'p-button-primary' : 'p-button-outlined'}
                      onClick={() => setCustomerInputMode('manual')}
                      size="small"
                    />
                  </div>

                  {customerInputMode === 'select' ? (
                    <Dropdown
                      id="customer"
                      value={selectedCustomer}
                      options={customers}
                      onChange={(e) => setSelectedCustomer(e.value)}
                      optionLabel="email"
                      filter
                      filterBy="firstName,lastName,email"
                      placeholder="Search by name or email..."
                      emptyFilterMessage="No customers found"
                      emptyMessage="No customers available"
                      className="w-full customer-select-dropdown"
                      itemTemplate={(customer: Customer) => (
                        <div className="customer-option">
                          <span className="customer-name">{customer.firstName} {customer.lastName}</span>
                          <span className="customer-email">{customer.email}</span>
                        </div>
                      )}
                      valueTemplate={(customer: Customer | null) =>
                        customer ? (
                          <div className="customer-option selected">
                            <span className="customer-name">{customer.firstName} {customer.lastName}</span>
                            <span className="customer-email">{customer.email}</span>
                          </div>
                        ) : (
                          <span className="placeholder-text">Search by name or email...</span>
                        )
                      }
                    />
                  ) : (
                    <div className="manual-customer-inputs">
                      <div className="p-field">
                        <label htmlFor="manual-email">Email *</label>
                        <InputText
                          id="manual-email"
                          value={manualEmail}
                          onChange={(e) => setManualEmail(e.target.value)}
                          placeholder="customer@example.com"
                          className="w-full"
                        />
                      </div>
                      <div className="p-field-group">
                        <div className="p-field">
                          <label htmlFor="manual-firstname">First Name</label>
                          <InputText
                            id="manual-firstname"
                            value={manualFirstName}
                            onChange={(e) => setManualFirstName(e.target.value)}
                            placeholder="John"
                          />
                        </div>
                        <div className="p-field">
                          <label htmlFor="manual-lastname">Last Name</label>
                          <InputText
                            id="manual-lastname"
                            value={manualLastName}
                            onChange={(e) => setManualLastName(e.target.value)}
                            placeholder="Doe"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <small className="field-hint">
                    <i className="pi pi-info-circle" style={{ marginRight: '0.25rem' }} />
                    The subscription will be applied to this customer immediately after payment
                  </small>
                </div>
              </div>
            </Card>

            <Card className="gift-summary-card">
              <h2>Order Summary</h2>
              <Divider />

              <div className="summary-details">
                <div className="summary-row">
                  <span>Gift Type:</span>
                  <span className="summary-value">{selectedProduct?.name}</span>
                </div>
                <div className="summary-row">
                  <span>Price:</span>
                  <span className="summary-value">
                    {selectedProduct ? formatPrice(selectedProduct.amount) : '$0.00'}
                  </span>
                </div>
                {customerInputMode === 'select' && selectedCustomer && (
                  <div className="summary-row">
                    <span>Customer:</span>
                    <span className="summary-value">
                      {selectedCustomer.firstName} {selectedCustomer.lastName}
                    </span>
                  </div>
                )}
                {customerInputMode === 'manual' && manualEmail && (
                  <div className="summary-row">
                    <span>Customer:</span>
                    <span className="summary-value">
                      {manualFirstName || manualLastName
                        ? `${manualFirstName} ${manualLastName}`.trim()
                        : manualEmail}
                    </span>
                  </div>
                )}
              </div>

              <Divider />

              <div className="summary-total">
                <span>Total Amount:</span>
                <span className="total-value">{formatPrice(totalAmount)}</span>
              </div>

              <Button
                label={processing ? 'Processing...' : 'Purchase Gift'}
                icon={processing ? 'pi pi-spin pi-spinner' : 'pi pi-shopping-cart'}
                onClick={handlePurchase}
                disabled={processing || !selectedProduct || !hasValidCustomer()}
                className="p-button-success purchase-button"
              />

              <div className="info-box">
                <i className="pi pi-check-circle" style={{ color: 'var(--green-500)' }} />
                <div>
                  <strong>Instant Activation</strong>
                  <p>
                    After payment, the subscription or assessment will be applied to your customer's
                    account immediately. They'll see a notification in their mobile app.
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* Purchase History Section */}
          <Card className="purchase-history-card" style={{ marginTop: '2rem' }}>
            <h2>
              <i className="pi pi-history" style={{ marginRight: '0.5rem' }} />
              Purchase History
            </h2>
            <Divider />

            {loadingPurchases ? (
              <div className="loading-container" style={{ padding: '2rem' }}>
                <ProgressSpinner style={{ width: '40px', height: '40px' }} />
                <p>Loading purchase history...</p>
              </div>
            ) : purchases.length === 0 ? (
              <Message
                severity="info"
                text="No gift purchases yet. Your purchase history will appear here."
                style={{ width: '100%' }}
              />
            ) : (
              <DataTable
                value={purchases}
                paginator
                rows={10}
                rowsPerPageOptions={[5, 10, 25]}
                emptyMessage="No purchases found"
                sortField="createdAt"
                sortOrder={-1}
                responsiveLayout="scroll"
              >
                <Column
                  field="createdAt"
                  header="Date"
                  body={dateTemplate}
                  sortable
                  style={{ width: '120px' }}
                />
                <Column
                  field="giftType"
                  header="Gift Type"
                  body={giftTypeTemplate}
                  sortable
                />
                <Column
                  header="Recipient"
                  body={recipientTemplate}
                />
                <Column
                  field="status"
                  header="Status"
                  body={statusTemplate}
                  sortable
                  style={{ width: '100px' }}
                />
                <Column
                  field="totalAmount"
                  header="Amount"
                  body={amountTemplate}
                  sortable
                  style={{ width: '100px' }}
                />
              </DataTable>
            )}
          </Card>
        </>
      )}
    </div>
  );
};

export default GiftPurchasePage;
